import { createSlice } from "@reduxjs/toolkit";

export const entityNotificationsKey = (entityType, entityId) =>
  `${entityType}:${String(entityId)}`;

export const INITIAL_STATE = {
  byEntity: {},
  unreadByEntity: {},
  workspaceFeed: {
    notifications: [],
    offset: 0,
    pageSize: 20,
    hasMore: false,
    isLoading: false,
    error: null,
    unreadOnly: false,
    requestId: 0,
  },
  unreadCount: null,
  optimisticallyReadIds: [],
};

const isStaleRequest = (state, requestId) =>
  requestId !== undefined && requestId !== state.workspaceFeed.requestId;

const applyUnreadDelta = (state, delta) => {
  if (typeof state.unreadCount === "number") {
    state.unreadCount = Math.max(0, state.unreadCount + delta);
  }
};

// Under the unread filter the server drops a row the moment it is read, so the cursor has to
// shift with it or the next Load More skips that many rows.
const shiftUnreadCursor = (state, delta) => {
  const feed = state.workspaceFeed;
  if (feed.unreadOnly) {
    feed.offset = Math.max(0, feed.offset + delta);
  }
};

const forgetOptimisticRead = (state, id) => {
  const wasPending = state.optimisticallyReadIds.includes(id);
  state.optimisticallyReadIds = state.optimisticallyReadIds.filter(
    (readId) => readId !== id
  );
  return wasPending;
};

const applyEntityUnreadDelta = (state, key, delta) => {
  const current = state.unreadByEntity?.[key];
  if (typeof current === "number") {
    state.unreadByEntity[key] = Math.max(0, current + delta);
  }
};

// One notification can sit in both the workspace feed and an entity feed; keeping the copies in
// step is what stops a single read being counted twice.
const setReadInEntityFeeds = (state, id, read) => {
  Object.keys(state.byEntity || {}).forEach((key) => {
    let changed = false;
    state.byEntity[key] = state.byEntity[key].map((notification) => {
      if (notification.id !== id || notification.read === read) {
        return notification;
      }
      changed = true;
      return { ...notification, read };
    });
    if (changed) {
      applyEntityUnreadDelta(state, key, read ? -1 : 1);
    }
  });
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState: INITIAL_STATE,
  reducers: {
    setEntityNotifications: (state, action) => {
      const { entityType, entityId, notifications } = action.payload;
      state.byEntity[entityNotificationsKey(entityType, entityId)] =
        notifications;
    },
    setEntityUnreadCount: (state, action) => {
      const { entityType, entityId, count } = action.payload;
      state.unreadByEntity[entityNotificationsKey(entityType, entityId)] =
        count;
    },
    markEntityNotificationRead: (state, action) => {
      const { entityType, entityId, id } = action.payload;
      const key = entityNotificationsKey(entityType, entityId);
      const notifications = state.byEntity[key];
      if (!notifications) {
        return;
      }
      const wasUnread = notifications.some(
        (notification) => notification.id === id && !notification.read
      );
      state.byEntity[key] = notifications.map((notification) =>
        notification.id === id
          ? { ...notification, read: true }
          : notification
      );
      if (wasUnread) {
        applyUnreadDelta(state, -1);
        applyEntityUnreadDelta(state, key, -1);
      }
      const workspaceNotification = state.workspaceFeed?.notifications?.find(
        (notification) => notification.id === id
      );
      if (workspaceNotification && !workspaceNotification.read) {
        workspaceNotification.read = true;
        shiftUnreadCursor(state, -1);
      }
    },
    resetWorkspaceFeed: (state) => {
      state.workspaceFeed = INITIAL_STATE.workspaceFeed;
    },
    startWorkspaceFeedRequest: (state, action) => {
      state.workspaceFeed.requestId = action.payload;
      state.workspaceFeed.isLoading = true;
    },
    finishWorkspaceFeedRequest: (state, action) => {
      if (state.workspaceFeed.requestId === action.payload) {
        state.workspaceFeed.isLoading = false;
      }
    },
    setWorkspaceFeedPage: (state, action) => {
      const {
        notifications,
        offset,
        pageSize,
        hasMore,
        append,
        isRead,
        requestId,
      } = action.payload;
      if (isStaleRequest(state, requestId)) {
        return;
      }
      state.workspaceFeed.notifications = append
        ? [...state.workspaceFeed.notifications, ...notifications]
        : notifications;
      // The API echoes the requested offset, so advance past the rows just received.
      state.workspaceFeed.offset = offset + notifications.length;
      state.workspaceFeed.pageSize = pageSize;
      state.workspaceFeed.hasMore = hasMore;
      state.workspaceFeed.unreadOnly = isRead === false;
      state.workspaceFeed.error = null;
    },
    setWorkspaceFeedError: (state, action) => {
      const { message, requestId } = action.payload;
      if (isStaleRequest(state, requestId)) {
        return;
      }
      state.workspaceFeed.error = message;
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
      state.optimisticallyReadIds = [];
    },
    applyWorkspaceNotificationRead: (state, action) => {
      const id = action.payload;
      const notification = state.workspaceFeed.notifications.find(
        (n) => n.id === id
      );
      if (notification && !notification.read) {
        notification.read = true;
        notification.readAt = new Date().toISOString();
        applyUnreadDelta(state, -1);
        shiftUnreadCursor(state, -1);
        if (!state.optimisticallyReadIds.includes(id)) {
          state.optimisticallyReadIds.push(id);
        }
        setReadInEntityFeeds(state, id, true);
      }
    },
    applyWorkspaceNotificationUnread: (state, action) => {
      const id = action.payload;
      const notification = state.workspaceFeed.notifications.find(
        (n) => n.id === id
      );
      if (!notification) {
        return;
      }
      const wasRead = notification.read;
      notification.read = false;
      notification.readAt = null;
      if (forgetOptimisticRead(state, id)) {
        applyUnreadDelta(state, 1);
      }
      if (wasRead) {
        shiftUnreadCursor(state, 1);
      }
      setReadInEntityFeeds(state, id, false);
    },
  },
});

export const {
  setEntityNotifications,
  setEntityUnreadCount,
  markEntityNotificationRead,
  resetWorkspaceFeed,
  startWorkspaceFeedRequest,
  finishWorkspaceFeedRequest,
  setWorkspaceFeedPage,
  setWorkspaceFeedError,
  setUnreadCount,
  applyWorkspaceNotificationRead,
  applyWorkspaceNotificationUnread,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
